import { supabase } from '@/shared/supabase/client'
import { resolveAuthUserAfterRefresh } from '@/shared/supabase/resolveAuthUserAfterRefresh'
import { isScreenshotFixtureMode } from '@/shared/lib/screenshotMode'
import { screenshotFixturePatients } from '@/shared/screenshotSeed/synapScreenshotFixtures'
import { normalizeIdentity } from '@/shared/lib/normalize'
import { calculateAgeFromBirthDate, normalizePatientSex } from '@/shared/lib/patient'
import { AppError } from '@/shared/lib/appError'
import { toRepositoryError } from '@/shared/repositories/common/repositoryError'
import type { Database } from '@/shared/supabase/database.types'
import { toPatientPhoneE164 } from '@/shared/lib/whatsappShare'

export interface RepoPatient {
  id: string
  name: string
  normalizedName: string
  tags: string[]
  documentNumber?: string
  email?: string
  /** PAC-06: E.164 (`+55…`). */
  phone?: string
  sex?: string
  birthDate?: string
  age?: number
  city?: string
  summary?: string
  comment?: string
  attendanceTypeId?: string
  attendanceTypeName?: string
  isGestatingOrLactating?: boolean | null
  lastWeightKg?: number
  lastWeightAt?: string
  createdAt: string
  updatedAt: string
}

export interface DuplicateMatch {
  inputName: string
  matches: RepoPatient[]
}

export type PatientRelationship = 'self' | 'child' | 'parent' | 'spouse' | 'other'
export type PatientLinkRole = 'owner' | 'caregiver'

export interface UpsertPayload {
  name?: string
  tags?: string[]
  documentNumber?: string
  email?: string
  /** PAC-06: texto digitado ou E.164; vazio/null apaga. */
  phone?: string | null
  city?: string
  sex?: string
  birthDate?: string
  relationship?: PatientRelationship
  linkRole?: PatientLinkRole
  summary?: string
  comment?: string
  attendanceTypeId?: string | null
  isGestatingOrLactating?: boolean | null
  lastWeightKg?: number | null
}

type ProfileRole = 'doctor' | 'patient' | 'admin'
interface CurrentActor {
  id: string
  role: ProfileRole
  isSystemAdmin: boolean
}

/** Permite passar o actor para evitar chamadas de auth (reduz LockManager contention). */
export type OptionalActor = Pick<CurrentActor, 'id' | 'role' | 'isSystemAdmin'>

const canBypassOwnerScope = (actor: CurrentActor): boolean => {
  void actor
  return false
}

const normalizeEmail = (value: string | undefined): string | undefined => {
  const cleanValue = value?.trim().toLowerCase()
  return cleanValue || undefined
}

/** `undefined` = campo não enviado (não mexe); `null` = apagar. Número inválido é erro de validação. */
const normalizePhone = (value: string | null | undefined): string | null | undefined => {
  if (value === undefined) return undefined
  const phone = toPatientPhoneE164(value)
  if (phone === undefined) {
    throw new AppError('Telefone inválido. Use DDD e número, como (11) 98765-4421.', { status: 422, code: 'VALIDATION' })
  }
  return phone
}

const parseTagsFromRow = (row: { tags?: unknown }): string[] => {
  const t = row.tags
  if (Array.isArray(t)) {
    return t.filter((x): x is string => typeof x === 'string')
  }
  return []
}

type PatientRow = Database['public']['Tables']['patients']['Row'] & {
  summary?: string | null
  comment?: string | null
  tags?: unknown
  attendance_type_id?: string | null
  attendance_types?: { name: string } | null
  is_gestating_or_lactating?: boolean | null
  last_weight_kg?: number | null
  last_weight_at?: string | null
}

const toRepoPatient = (row: PatientRow): RepoPatient => {
  const normalizedSex = normalizePatientSex(row.sex ?? undefined)
  const birthDate = row.birth_date ?? undefined

  return {
    id: row.id,
    name: row.full_name,
    normalizedName: row.normalized_name,
    tags: parseTagsFromRow(row),
    documentNumber: row.document_number ?? undefined,
    email: row.email ?? undefined,
    phone: row.phone ?? undefined,
    sex: normalizedSex,
    birthDate,
    age: birthDate ? calculateAgeFromBirthDate(birthDate) ?? undefined : undefined,
    city: row.city ?? undefined,
    summary: row.summary ?? undefined,
    comment: row.comment ?? undefined,
    attendanceTypeId: row.attendance_type_id ?? undefined,
    attendanceTypeName: row.attendance_types?.name ?? undefined,
    isGestatingOrLactating: row.is_gestating_or_lactating ?? undefined,
    lastWeightKg:
      row.last_weight_kg != null && Number(row.last_weight_kg) > 0
        ? Number(row.last_weight_kg)
        : undefined,
    lastWeightAt: row.last_weight_at ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

/**
 * Obtém o usuário autenticado. Prefere getUser() (valida no servidor) para evitar
 * sessão em cache desatualizada após logout/login. getSession() pode retornar
 * dados em cache que não refletem o estado atual.
 */
const getCurrentUser = async () => {
  const tryGetUser = async () => {
    const { data, error } = await supabase.auth.getUser()
    if (error) throw error
    if (!data.user) throw new Error('Usuário não autenticado.')
    return data.user
  }

  try {
    return await tryGetUser()
  } catch (firstErr) {
    const msg = (firstErr instanceof Error ? firstErr.message : String(firstErr ?? '')).toLowerCase()
    const isSessionError =
      msg.includes('session') ||
      msg.includes('refresh') ||
      msg.includes('missing') ||
      msg.includes('auth') ||
      msg.includes('invalid') ||
      msg.includes('jwt') ||
      msg.includes('token') ||
      msg.includes('expired') ||
      msg.includes('autenticado')
    if (isSessionError) {
      const { error: refreshErr } = await supabase.auth.refreshSession()
      if (!refreshErr) {
        return await resolveAuthUserAfterRefresh(
          tryGetUser,
          'Sessão expirada ou inválida. Faça login novamente.',
        )
      }
    }
    throw toRepositoryError(
      firstErr,
      'Sessão expirada ou inválida. Faça login novamente.',
      401,
    )
  }
}

const ensureProfile = async (user: { id: string; email?: string; user_metadata?: unknown }) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, role, is_system_admin')
    .eq('id', user.id)
    .maybeSingle()

  if (error) {
    throw toRepositoryError(error, 'Não foi possível obter o perfil do usuário.')
  }

  if (data) {
    return {
      id: data.id,
      role: data.role as ProfileRole,
      isSystemAdmin: Boolean(data.is_system_admin),
    }
  }

  const fallbackRole =
    typeof user.user_metadata === 'object' &&
    user.user_metadata &&
    'role' in (user.user_metadata as Record<string, unknown>) &&
    (user.user_metadata as Record<string, unknown>).role === 'doctor'
      ? 'doctor'
      : 'patient'

  const email = normalizeEmail(user.email) ?? `${user.id}@invalid.local`
  const displayName =
    typeof user.user_metadata === 'object' &&
    user.user_metadata &&
    'display_name' in (user.user_metadata as Record<string, unknown>) &&
    typeof (user.user_metadata as Record<string, unknown>).display_name === 'string'
      ? ((user.user_metadata as Record<string, unknown>).display_name as string)
      : email.split('@')[0]

  const { data: created, error: createError } = await supabase
    .from('profiles')
    .insert({
      id: user.id,
      email,
      role: fallbackRole,
      display_name: displayName,
    })
    .select('id, role, is_system_admin')
    .single()

  if (createError) {
    throw toRepositoryError(createError, 'Não foi possível criar o perfil base do usuário.')
  }

  return {
    id: created.id,
    role: created.role as ProfileRole,
    isSystemAdmin: Boolean(created.is_system_admin),
  }
}

const getCurrentActor = async (): Promise<CurrentActor> => {
  const user = await getCurrentUser()
  return ensureProfile(user)
}

const normalizeTags = (tags: string[] | undefined): string[] =>
  tags
    ? [...new Set(tags.map((t) => t.trim()).filter((t) => t.length > 0))]
    : []

const parseLastWeightKg = (value: number | null | undefined): number | null => {
  if (value == null || !Number.isFinite(value) || value <= 0) return null
  return Math.round(value * 100) / 100
}

const resolveWeightWriteFields = (
  payloadWeight: number | null | undefined,
  existingWeight: number | null | undefined,
  existingWeightAt: string | null | undefined,
): { last_weight_kg: number | null; last_weight_at: string | null } => {
  if (payloadWeight === undefined) {
    return {
      last_weight_kg: existingWeight ?? null,
      last_weight_at: existingWeightAt ?? null,
    }
  }

  const normalized = parseLastWeightKg(payloadWeight)
  const previous = parseLastWeightKg(existingWeight ?? null)

  if (normalized === previous) {
    return {
      last_weight_kg: normalized,
      last_weight_at: normalized != null ? existingWeightAt ?? new Date().toISOString() : null,
    }
  }

  return {
    last_weight_kg: normalized,
    last_weight_at: normalized != null ? new Date().toISOString() : null,
  }
}

const buildPatientWriteData = (payload: UpsertPayload) => {
  const name = payload.name?.trim() ?? ''
  const normalizedName = normalizeIdentity(name)

  return {
    name,
    normalizedName,
    documentNumber: payload.documentNumber?.trim() || undefined,
    email: normalizeEmail(payload.email),
    phone: normalizePhone(payload.phone),
    city: payload.city?.trim() || undefined,
    sex: normalizePatientSex(payload.sex),
    birthDate: payload.birthDate?.trim() || undefined,
    tags: normalizeTags(payload.tags),
    attendanceTypeId: payload.attendanceTypeId ?? undefined,
    isGestatingOrLactating: payload.isGestatingOrLactating ?? undefined,
    lastWeightKg:
      payload.lastWeightKg !== undefined ? parseLastWeightKg(payload.lastWeightKg) : undefined,
  }
}

const buildScopedPatientQuery = (actor: CurrentActor) => {
  const query = supabase.from('patients').select('*')
  return canBypassOwnerScope(actor) ? query : query.eq('owner_user_id', actor.id)
}

const findExistingPatient = async ({
  actor,
  documentNumber,
  email,
  normalizedName,
}: {
  actor: CurrentActor
  documentNumber?: string
  email?: string
  normalizedName: string
}) => {
  if (documentNumber) {
    const byDocument = await buildScopedPatientQuery(actor)
      .eq('document_number', documentNumber)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (byDocument.error) {
      throw toRepositoryError(byDocument.error, 'Não foi possível consultar paciente por documento.')
    }
    if (byDocument.data) return byDocument.data
  }

  if (email) {
    const byEmail = await buildScopedPatientQuery(actor)
      .ilike('email', email)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (byEmail.error) {
      throw toRepositoryError(byEmail.error, 'Não foi possível consultar paciente por email.')
    }
    if (byEmail.data) return byEmail.data
  }

  const byName = await buildScopedPatientQuery(actor)
    .eq('normalized_name', normalizedName)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (byName.error) {
    throw toRepositoryError(byName.error, 'Não foi possível consultar paciente por nome.')
  }

  return byName.data
}

export const patientsRepository = {
  list: async (): Promise<{ patients: RepoPatient[] }> => {
    if (isScreenshotFixtureMode()) {
      return { patients: screenshotFixturePatients() }
    }
    try {
      const actor = await getCurrentActor()
      let query = supabase
        .from('patients')
        .select('*, attendance_types(name)')
        .order('created_at', { ascending: false })

      if (!canBypassOwnerScope(actor)) {
        query = query.eq('owner_user_id', actor.id)
      }

      const { data, error } = await query
      if (error) {
        throw toRepositoryError(error, 'Não foi possível listar pacientes.')
      }

      return {
        patients: (data ?? []).map((row) => toRepoPatient(row as unknown as PatientRow)),
      }
    } catch (error) {
      throw toRepositoryError(error, 'Falha ao listar pacientes.')
    }
  },

  upsert: async (
    payload: UpsertPayload,
    options?: { actor?: OptionalActor },
  ): Promise<{ patient: RepoPatient; created: boolean }> => {
    try {
      const patientData = buildPatientWriteData(payload)
      if (!patientData.name) {
        throw new AppError('Nome do paciente é obrigatório.', {
          status: 422,
          code: 'VALIDATION',
        })
      }

      const actorFromOptions: CurrentActor | null = options?.actor
        ? {
            id: options.actor.id,
            role: options.actor.role as ProfileRole,
            isSystemAdmin: Boolean(options.actor.isSystemAdmin),
          }
        : null

      const runUpsertForActor = async (
        actor: CurrentActor,
      ): Promise<{ patient: RepoPatient; created: boolean }> => {
        const existing = await findExistingPatient({
          actor,
          documentNumber: patientData.documentNumber,
          email: patientData.email,
          normalizedName: patientData.normalizedName,
        })

        if (existing) {
          const updatePayload: Database['public']['Tables']['patients']['Update'] = {
            full_name: patientData.name,
            document_number: patientData.documentNumber ?? existing.document_number ?? null,
            email: patientData.email ?? existing.email ?? null,
            sex: patientData.sex ?? existing.sex,
            birth_date: patientData.birthDate ?? existing.birth_date,
            city: patientData.city ?? existing.city,
          }
          if (payload.lastWeightKg !== undefined) {
            const weightFields = resolveWeightWriteFields(
              payload.lastWeightKg,
              existing.last_weight_kg != null ? Number(existing.last_weight_kg) : null,
              existing.last_weight_at ?? null,
            )
            updatePayload.last_weight_kg = weightFields.last_weight_kg
            updatePayload.last_weight_at = weightFields.last_weight_at
          }
          if (payload.tags !== undefined) {
            updatePayload.tags = patientData.tags
          }
          if (patientData.phone !== undefined) {
            updatePayload.phone = patientData.phone
          }
          if (payload.attendanceTypeId !== undefined) {
            updatePayload.attendance_type_id = payload.attendanceTypeId ?? null
          }
          if (payload.isGestatingOrLactating !== undefined) {
            updatePayload.is_gestating_or_lactating = payload.isGestatingOrLactating ?? null
          }
          const { data, error } = await supabase
            .from('patients')
            .update(updatePayload)
            .eq('id', existing.id)
            .select('*, attendance_types(name)')
            .single()

          if (error) {
            throw toRepositoryError(error, 'Não foi possível atualizar o paciente.')
          }

          return {
            patient: toRepoPatient(data as unknown as PatientRow),
            created: false,
          }
        }

        const { data: newId, error: rpcError } = await supabase.rpc('insert_patient_as_owner', {
          p_full_name: patientData.name,
          p_normalized_name: patientData.normalizedName,
          p_document_number: patientData.documentNumber ?? null,
          p_email: patientData.email ?? null,
          p_sex: patientData.sex ?? null,
          p_birth_date: patientData.birthDate ?? null,
          p_city: patientData.city ?? null,
          p_tags: patientData.tags ?? [],
          p_attendance_type_id: patientData.attendanceTypeId ?? null,
          p_is_gestating_or_lactating: patientData.isGestatingOrLactating ?? null,
          p_last_weight_kg: patientData.lastWeightKg ?? null,
        })

        if (rpcError) {
          throw toRepositoryError(rpcError, 'Não foi possível criar o paciente.')
        }

        // O RPC de criação não conhece o telefone (PAC-06): grava logo depois, na mesma linha do dono.
        if (patientData.phone) {
          const { error: phoneError } = await supabase.from('patients').update({ phone: patientData.phone }).eq('id', newId)
          if (phoneError) throw toRepositoryError(phoneError, 'Não foi possível salvar o telefone do paciente.')
        }

        const { data: createdPatient, error: selectError } = await supabase
          .from('patients')
          .select('*, attendance_types(name)')
          .eq('id', newId)
          .single()

        if (selectError || !createdPatient) {
          throw toRepositoryError(
            selectError ?? new Error('Paciente criado mas não encontrado.'),
            'Não foi possível obter o paciente criado.',
          )
        }

        return {
          patient: toRepoPatient(createdPatient as unknown as PatientRow),
          created: true,
        }
      }

      const preferredActor = actorFromOptions ?? (await getCurrentActor())

      try {
        return await runUpsertForActor(preferredActor)
      } catch (firstError) {
        if (!actorFromOptions) {
          throw firstError
        }

        const normalizedError = toRepositoryError(firstError, 'Falha ao salvar paciente.')
        if (normalizedError.code !== 'UNAUTHORIZED') {
          throw firstError
        }

        // Actor vindo do estado local pode ficar stale sem token pronto no cliente.
        const refreshedActor = await getCurrentActor()
        return await runUpsertForActor(refreshedActor)
      }
    } catch (error) {
      throw toRepositoryError(error, 'Falha ao salvar paciente.')
    }
  },

  update: async (id: string, payload: UpsertPayload): Promise<{ patient: RepoPatient }> => {
    try {
      const actor = await getCurrentActor()
      const data = buildPatientWriteData(payload)
      const updateData: Database['public']['Tables']['patients']['Update'] = {}
      if (payload.name !== undefined && data.name) updateData.full_name = data.name
      if (payload.documentNumber !== undefined) updateData.document_number = data.documentNumber ?? null
      if (payload.email !== undefined) updateData.email = data.email ?? null
      if (data.phone !== undefined) updateData.phone = data.phone
      if (payload.sex !== undefined) updateData.sex = data.sex ?? null
      if (payload.birthDate !== undefined) updateData.birth_date = data.birthDate ?? null
      if (payload.city !== undefined) updateData.city = data.city ?? null
      if (payload.tags !== undefined) updateData.tags = data.tags
      if (payload.summary !== undefined) updateData.summary = payload.summary?.trim() || null
      if (payload.comment !== undefined) updateData.comment = payload.comment?.trim() || null
      if (payload.attendanceTypeId !== undefined) updateData.attendance_type_id = payload.attendanceTypeId ?? null
      if (payload.isGestatingOrLactating !== undefined) updateData.is_gestating_or_lactating = payload.isGestatingOrLactating ?? null
      if (payload.lastWeightKg !== undefined) {
        const { data: existingRow, error: existingError } = await buildScopedPatientQuery(actor)
          .eq('id', id)
          .maybeSingle()
        if (existingError) {
          throw toRepositoryError(existingError, 'Não foi possível consultar o paciente.')
        }
        const weightFields = resolveWeightWriteFields(
          payload.lastWeightKg,
          existingRow?.last_weight_kg != null ? Number(existingRow.last_weight_kg) : null,
          existingRow?.last_weight_at ?? null,
        )
        updateData.last_weight_kg = weightFields.last_weight_kg
        updateData.last_weight_at = weightFields.last_weight_at
      }

      let query = supabase
        .from('patients')
        .update(updateData)
        .eq('id', id)

      if (!canBypassOwnerScope(actor)) {
        query = query.eq('owner_user_id', actor.id)
      }

      const { data: updated, error } = await query.select('*, attendance_types(name)').single()
      if (error) {
        throw toRepositoryError(error, 'Não foi possível atualizar o paciente.')
      }

      return { patient: toRepoPatient(updated as unknown as PatientRow) }
    } catch (error) {
      throw toRepositoryError(error, 'Falha ao atualizar paciente.')
    }
  },

  deactivateForCurrentUser: async (patientId: string): Promise<void> => {
    try {
      const actor = await getCurrentActor()
      let query = supabase.from('patients').delete().eq('id', patientId)
      if (!canBypassOwnerScope(actor)) {
        query = query.eq('owner_user_id', actor.id)
      }

      const { error } = await query
      if (error) {
        throw toRepositoryError(error, 'Não foi possível remover paciente da lista atual.')
      }
    } catch (error) {
      throw toRepositoryError(error, 'Falha ao remover paciente da lista.')
    }
  },

  findDuplicates: async (names: string[]): Promise<{ duplicates: DuplicateMatch[] }> => {
    try {
      const normalizedToOriginal = new Map<string, string[]>()
      names
        .map((name) => name.trim())
        .filter((name) => name.length > 0)
        .forEach((name) => {
          const normalized = normalizeIdentity(name)
          const bucket = normalizedToOriginal.get(normalized)
          if (bucket) {
            bucket.push(name)
          } else {
            normalizedToOriginal.set(normalized, [name])
          }
        })

      const normalizedNames = [...normalizedToOriginal.keys()]
      if (!normalizedNames.length) {
        return { duplicates: [] }
      }

      const actor = await getCurrentActor()
      let query = supabase
        .from('patients')
        .select('*')
        .in('normalized_name', normalizedNames)

      if (!canBypassOwnerScope(actor)) {
        query = query.eq('owner_user_id', actor.id)
      }

      const { data, error } = await query
      if (error) {
        throw toRepositoryError(error, 'Não foi possível verificar duplicidade de pacientes.')
      }

      const byNormalized = new Map<string, RepoPatient[]>()
      ;(data ?? []).forEach((row) => {
        const patient = toRepoPatient(row)
        const bucket = byNormalized.get(patient.normalizedName)
        if (bucket) {
          bucket.push(patient)
        } else {
          byNormalized.set(patient.normalizedName, [patient])
        }
      })

      const duplicates: DuplicateMatch[] = []
      normalizedToOriginal.forEach((originalNames, normalizedName) => {
        const matches = byNormalized.get(normalizedName)
        if (!matches || !matches.length) return

        originalNames.forEach((inputName) => {
          duplicates.push({
            inputName,
            matches: [...matches].sort(
              (left, right) =>
                new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
            ),
          })
        })
      })

      return { duplicates }
    } catch (error) {
      throw toRepositoryError(error, 'Falha ao consultar duplicidades de pacientes.')
    }
  },
}
